import React, {
   useRef,
   useState,
   useCallback,
   useEffect,
   useLayoutEffect,
} from "react"
import "./NavigationController.scss"

const SCROLL_AMOUNT = 100
const PRESS_AND_HOLD_DELAY_MS = 0
// Separate step sizes and continuous speeds per axis to normalize feel
const HORIZONTAL_STEP = 60
const VERTICAL_STEP = 80
const HORIZONTAL_HOLD_SPEED_PX_PER_SEC = 500
const VERTICAL_HOLD_SPEED_PX_PER_SEC = 400

const isElementVerticallyScrollable = el => {
   if (!el) return false
   const scrollableBySize = el.scrollHeight > el.clientHeight
   if (!scrollableBySize) return false
   const style = window.getComputedStyle(el)
   const overflowY = style ? style.overflowY : ""
   // Treat auto/scroll/overlay as scrollable
   return (
      ["auto", "scroll", "overlay"].includes(overflowY) ||
      el === document.scrollingElement ||
      el === document.documentElement
   )
}

// Find a vertically scrollable ancestor starting from a point in the viewport
const findScrollableAncestorAtViewportCenter = () => {
   const x = Math.floor(window.innerWidth / 2)
   const y = Math.floor(window.innerHeight / 2)
   let el = document.elementFromPoint(x, y)
   while (el && el !== document.body && el !== document.documentElement) {
      if (isElementVerticallyScrollable(el)) return el
      el = el.parentElement
   }
   return null
}

const NavigationController = ({
   position = "bottom-right",
   // Backward compatibility: if only one ref is provided, use it for both axes
   scrollContainerRef,
   horizontalRef,
   verticalRef,
}) => {
   const rootRef = useRef(null)
   const dragRef = useRef(null)
   const [isDragging, setIsDragging] = useState(false)
   const [startPos, setStartPos] = useState({ x: 0, y: 0 })
   const [coords, setCoords] = useState({ top: null, left: null })
   const [dragStartOffset, setDragStartOffset] = useState({ top: 0, left: 0 })

   // Compute initial position based on `position` prop
   useLayoutEffect(() => {
      const node = rootRef.current
      if (!node) return
      const selfRect = node.getBoundingClientRect()

      const margin = 16
      let top = margin
      let left = margin

      const viewportW = window.innerWidth
      const viewportH = window.innerHeight

      if (position.includes("bottom"))
         top = viewportH - selfRect.height - margin
      if (position.includes("right")) left = viewportW - selfRect.width - margin
      if (position.includes("top")) top = margin
      if (position.includes("left")) left = margin

      // Clamp within viewport
      top = Math.max(0, Math.min(top, viewportH - selfRect.height))
      left = Math.max(0, Math.min(left, viewportW - selfRect.width))
      setCoords({ top, left })
   }, [position])

   const getHorizontalEl = () =>
      (horizontalRef && horizontalRef.current) ||
      (scrollContainerRef && scrollContainerRef.current) ||
      null

   const resolveVerticalTarget = () => {
      const explicit =
         (verticalRef && verticalRef.current) ||
         (scrollContainerRef && scrollContainerRef.current)
      if (isElementVerticallyScrollable(explicit)) return explicit

      // Try ancestors of the explicit ref first (if provided)
      if (explicit) {
         let el = explicit.parentElement
         while (el && el !== document.body && el !== document.documentElement) {
            if (isElementVerticallyScrollable(el)) return el
            el = el.parentElement
         }
      }

      // Hit-test: find the scrollable container under the viewport center
      const fromCenter = findScrollableAncestorAtViewportCenter()
      if (fromCenter) return fromCenter

      // Final fallback to the document scroller
      return document.scrollingElement || document.documentElement
   }

   const handleCenterMouseDown = e => {
      // Start dragging the controller (reposition within parent)
      e.preventDefault()
      setIsDragging(true)
      setStartPos({ x: e.clientX, y: e.clientY })
      setDragStartOffset({ top: coords.top || 0, left: coords.left || 0 })
   }

   const handleScroll = (dx, dy) => {
      const horizontalEl = getHorizontalEl()
      const verticalEl = resolveVerticalTarget()

      if (dx !== 0 && horizontalEl) {
         horizontalEl.scrollTo({
            left: horizontalEl.scrollLeft + dx,
            top: horizontalEl.scrollTop,
            behavior: "auto",
         })
      }

      if (dy !== 0) {
         const isDocumentTarget =
            !verticalEl ||
            verticalEl === document.scrollingElement ||
            verticalEl === document.documentElement ||
            verticalEl === document.body

         if (isDocumentTarget) {
            window.scrollBy({ top: dy, behavior: "auto" })
         } else {
            verticalEl.scrollTo({
               top: verticalEl.scrollTop + dy,
               left: verticalEl.scrollLeft,
               behavior: "auto",
            })
         }
      }
   }

   // Press-and-hold support
   const holdStateRef = useRef({
      active: false,
      dxSign: 0,
      dySign: 0,
      rafId: null,
      lastTs: 0,
   })
   const HOLD_SPEED_PX_PER_SEC = 800

   const holdTick = ts => {
      const st = holdStateRef.current
      if (!st.active) return
      const elapsed = st.lastTs ? ts - st.lastTs : 16
      st.lastTs = ts
      const dxDistance = (HORIZONTAL_HOLD_SPEED_PX_PER_SEC * elapsed) / 1000
      const dyDistance = (VERTICAL_HOLD_SPEED_PX_PER_SEC * elapsed) / 1000
      const dx = st.dxSign * dxDistance
      const dy = st.dySign * dyDistance
      handleScroll(dx, dy)
      st.rafId = requestAnimationFrame(holdTick)
   }

   const startHold = (dxSign, dySign) => {
      const st = holdStateRef.current
      st.active = true
      st.dxSign = dxSign
      st.dySign = dySign
      st.lastTs = 0

      // Always perform a small discrete step to provide immediate feedback
      const stepX = dxSign * HORIZONTAL_STEP
      const stepY = dySign * VERTICAL_STEP
      if (stepX !== 0 || stepY !== 0) handleScroll(stepX, stepY)

      // Start continuous scroll (immediately if delay is 0)
      if (st.rafId) cancelAnimationFrame(st.rafId)
      const startContinuous = () => {
         if (!st.active) return
         st.rafId = requestAnimationFrame(holdTick)
      }
      st.startTimer && clearTimeout(st.startTimer)
      if (PRESS_AND_HOLD_DELAY_MS > 0) {
         st.startTimer = setTimeout(startContinuous, PRESS_AND_HOLD_DELAY_MS)
      } else {
         // Begin on the next frame to avoid an initial jerk
         requestAnimationFrame(startContinuous)
      }
      window.addEventListener("mouseup", stopHold, { once: true })
      window.addEventListener("touchend", stopHold, { once: true })
      window.addEventListener("blur", stopHold, { once: true })
      window.addEventListener("mouseleave", stopHold, { once: true })
   }

   const stopHold = () => {
      const st = holdStateRef.current
      st.active = false
      if (st.rafId) cancelAnimationFrame(st.rafId)
      st.rafId = null
      if (st.startTimer) {
         clearTimeout(st.startTimer)
         st.startTimer = null
      }
   }

   const handleMouseMove = useCallback(
      e => {
         if (!isDragging) return

         const node = rootRef.current
         if (!node) return

         const selfRect = node.getBoundingClientRect()

         const dx = e.clientX - startPos.x
         const dy = e.clientY - startPos.y

         let left = dragStartOffset.left + dx
         let top = dragStartOffset.top + dy

         // Clamp within viewport
         const viewportW = window.innerWidth
         const viewportH = window.innerHeight
         left = Math.max(0, Math.min(left, viewportW - selfRect.width))
         top = Math.max(0, Math.min(top, viewportH - selfRect.height))

         setCoords({ top, left })
      },
      [isDragging, startPos, dragStartOffset]
   )

   const handleMouseUp = () => setIsDragging(false)

   useEffect(() => {
      if (isDragging) {
         window.addEventListener("mousemove", handleMouseMove)
         window.addEventListener("mouseup", handleMouseUp)
      } else {
         window.removeEventListener("mousemove", handleMouseMove)
         window.removeEventListener("mouseup", handleMouseUp)
      }
      return () => {
         window.removeEventListener("mousemove", handleMouseMove)
         window.removeEventListener("mouseup", handleMouseUp)
      }
   }, [isDragging, handleMouseMove])

   const inlineStyle = {
      position: "fixed",
      touchAction: "none",
      cursor: isDragging ? "grabbing" : "default",
      userSelect: "none",
      zIndex: 1000,
      ...(coords.top != null && coords.left != null
         ? { top: coords.top, left: coords.left }
         : {
              ...(position.includes("bottom") ? { bottom: 16 } : { top: 16 }),
              ...(position.includes("right") ? { right: 16 } : { left: 16 }),
           }),
   }

   return (
      <div
         className={`navigation-controller ${position}`}
         style={inlineStyle}
         ref={rootRef}
      >
         <button
            className="nav-btn up"
            onMouseDown={e => {
               e.preventDefault()
               startHold(0, -1)
            }}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={e => {
               e.preventDefault()
               startHold(0, -1)
            }}
            onTouchEnd={stopHold}
         >
            ▲
         </button>
         <div className="middle-row">
            <button
               className="nav-btn left"
               onMouseDown={e => {
                  e.preventDefault()
                  startHold(-1, 0)
               }}
               onMouseUp={stopHold}
               onMouseLeave={stopHold}
               onTouchStart={e => {
                  e.preventDefault()
                  startHold(-1, 0)
               }}
               onTouchEnd={stopHold}
            >
               ◀
            </button>
            <div
               className={`drag-indicator ${isDragging ? "dragging" : ""}`}
               ref={dragRef}
               onMouseDown={handleCenterMouseDown}
               title="Drag to reposition"
            >
               ⬤
            </div>
            <button
               className="nav-btn right"
               onMouseDown={e => {
                  e.preventDefault()
                  startHold(1, 0)
               }}
               onMouseUp={stopHold}
               onMouseLeave={stopHold}
               onTouchStart={e => {
                  e.preventDefault()
                  startHold(1, 0)
               }}
               onTouchEnd={stopHold}
            >
               ▶
            </button>
         </div>
         <button
            className="nav-btn down"
            onMouseDown={e => {
               e.preventDefault()
               startHold(0, 1)
            }}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={e => {
               e.preventDefault()
               startHold(0, 1)
            }}
            onTouchEnd={stopHold}
         >
            ▼
         </button>
      </div>
   )
}

export default NavigationController
